import React, { useContext, useMemo } from 'react';
import { ThemeContext } from 'styled-components';
import { format } from 'date-fns';
import {
  calcChartStats,
  formatDuration,
  classifyCorrelation,
} from '../../utils';
import * as s from './styled-chart-stats-panel';

// Formatador "smart" pra valores numericos.
const formatNum = (v) => {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) < 100) return v.toFixed(2);
  if (Math.abs(v) < 1000) return v.toFixed(1);
  return Math.round(v).toString();
};

const formatTs = (ts) => {
  if (!ts) return '';
  return format(new Date(ts * 1000), 'dd/MM HH:mm');
};

// Icones lucide-style inline. Mantemos os paths inline pra nao adicionar
// dependencia e seguir o padrao do projeto.
const IconTrendUp = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
    <polyline points="16 7 22 7 22 13" />
  </svg>
);
const IconTrendDown = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
    <polyline points="16 17 22 17 22 11" />
  </svg>
);
const IconTrendFlat = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <line x1="3" y1="12" x2="21" y2="12" />
    <polyline points="17 8 21 12 17 16" />
  </svg>
);
const IconArrowUp = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="5 12 12 5 19 12" />
  </svg>
);
const IconArrowDown = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <polyline points="19 12 12 19 5 12" />
  </svg>
);
const IconAlert = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IconNote = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

// Classifica tendencia em estavel/subindo/descendo. Threshold em
// relacao a stddev pra ser robusto a unidade.
const classifyTrend = (slopePerHour, stddev, colors) => {
  if (!Number.isFinite(slopePerHour)) {
    return {
      label: 'Sem dados',
      Icon: IconTrendFlat,
      color: colors.label,
      isStable: true,
    };
  }
  const threshold = Math.max(Math.abs(stddev) * 0.1, 0.001);
  if (Math.abs(slopePerHour) < threshold) {
    return {
      label: 'Estável',
      Icon: IconTrendFlat,
      color: colors.label,
      isStable: true,
    };
  }
  if (slopePerHour > 0) {
    return {
      label: 'Subindo',
      Icon: IconTrendUp,
      color: colors.orange || colors.lightblue,
      isStable: false,
    };
  }
  return {
    label: 'Descendo',
    Icon: IconTrendDown,
    color: colors.lightblue,
    isStable: false,
  };
};

// Panel de estatisticas pro modo avancado do LineChart.
const ChartStatsPanel = ({
  data,
  ranges,
  unit,
  stats: precomputedStats,
  correlations,
  primaryVarName,
  annotations,
  onRemoveAnnotation,
  operationalAnalysis,
}) => {
  const { colors } = useContext(ThemeContext);
  const stats = useMemo(
    () =>
      precomputedStats !== undefined
        ? precomputedStats
        : calcChartStats(data, ranges),
    [precomputedStats, data, ranges]
  );
  const trendInfo = useMemo(
    () =>
      stats && stats.trend
        ? classifyTrend(stats.trend.slopePerHour, stats.stddev, colors)
        : null,
    [stats, colors]
  );

  // Posicoes (em %) dos quartis e mediana sobre o eixo min-max — usadas
  // pelo RangeBar visual. Quando min === max, retorna posicoes seguras.
  const rangeGeom = useMemo(() => {
    if (!stats) return null;
    const span = stats.max - stats.min;
    if (!Number.isFinite(span) || span <= 0) {
      return { q1Pct: 25, q3Pct: 75, medianPct: 50 };
    }
    const pctOf = (v) => ((v - stats.min) / span) * 100;
    return {
      q1Pct: pctOf(stats.q1),
      q3Pct: pctOf(stats.q3),
      medianPct: pctOf(stats.median),
    };
  }, [stats]);

  if (!stats) {
    return (
      <s.Container>
        <s.EmptyMessage>
          Aguardando dados pra calcular estatísticas...
        </s.EmptyMessage>
      </s.Container>
    );
  }

  const TrendIcon = trendInfo ? trendInfo.Icon : null;

  return (
    <s.Container>
      {/* HERO — valor central (media) + tendencia em destaque. */}
      <s.Hero>
        <s.HeroLabel>Média do período</s.HeroLabel>
        <s.HeroValue>
          {formatNum(stats.mean)}
          {unit && <small>{unit}</small>}
        </s.HeroValue>
        {trendInfo && stats.trend && (
          <s.TrendChip color={trendInfo.color}>
            <TrendIcon />
            <span>{trendInfo.label}</span>
            {!trendInfo.isStable && Number.isFinite(stats.trend.slopePerHour) && (
              <span className="magnitude">
                {stats.trend.slopePerHour >= 0 ? '+' : ''}
                {formatNum(stats.trend.slopePerHour)}
                {unit ? ` ${unit}` : ''}/h
              </span>
            )}
          </s.TrendChip>
        )}
      </s.Hero>

      {/* FAIXA — RangeBar visual com Q1-Q3 destacado + mediana */}
      {rangeGeom && (
        <s.Group>
          <s.GroupTitle>Faixa de operação</s.GroupTitle>
          <s.RangeBox>
            <s.RangeBarTrack
              title={`Caixa: 50% centrais (Q1 ${formatNum(stats.q1)} → Q3 ${formatNum(
                stats.q3
              )}). Linha: mediana ${formatNum(stats.median)}.`}
            >
              <s.RangeBarBox
                $startPct={rangeGeom.q1Pct}
                $endPct={rangeGeom.q3Pct}
              />
              <s.RangeBarMedian $pct={rangeGeom.medianPct} />
            </s.RangeBarTrack>
            <s.RangeBarLabels>
              <span>
                mín <strong>{formatNum(stats.min)}</strong>
                {unit ? ` ${unit}` : ''}
              </span>
              <span>
                máx <strong>{formatNum(stats.max)}</strong>
                {unit ? ` ${unit}` : ''}
              </span>
            </s.RangeBarLabels>
            <s.RangeBarSpread
              title="Amplitude: diferença entre máximo e mínimo no período"
            >
              <span>variação típica</span>
              <strong>
                ±{formatNum(stats.stddev)}
                {unit ? ` ${unit}` : ''}
              </strong>
            </s.RangeBarSpread>
          </s.RangeBox>
        </s.Group>
      )}

      {/* EXTREMOS — quando aconteceu o pico e o vale */}
      {(stats.minTimestamp || stats.maxTimestamp) && (
        <s.Group>
          <s.GroupTitle>Pico e vale</s.GroupTitle>
          {stats.maxTimestamp && (
            <s.ExtremeRow>
              <s.ExtremeIcon color={colors.orange || colors.red}>
                <IconArrowUp />
              </s.ExtremeIcon>
              <s.ExtremeInfo>
                <s.ExtremeLabel>Mais alto</s.ExtremeLabel>
                <s.ExtremeValueLine>
                  <strong>{formatNum(stats.max)}</strong>
                  {unit && <small>{unit}</small>}
                  <span className="when">{formatTs(stats.maxTimestamp)}</span>
                </s.ExtremeValueLine>
              </s.ExtremeInfo>
            </s.ExtremeRow>
          )}
          {stats.minTimestamp && (
            <s.ExtremeRow>
              <s.ExtremeIcon color={colors.lightblue}>
                <IconArrowDown />
              </s.ExtremeIcon>
              <s.ExtremeInfo>
                <s.ExtremeLabel>Mais baixo</s.ExtremeLabel>
                <s.ExtremeValueLine>
                  <strong>{formatNum(stats.min)}</strong>
                  {unit && <small>{unit}</small>}
                  <span className="when">{formatTs(stats.minTimestamp)}</span>
                </s.ExtremeValueLine>
              </s.ExtremeInfo>
            </s.ExtremeRow>
          )}
        </s.Group>
      )}

      {/* PROJEÇÃO — so quando ha tendencia nao-estavel */}
      {stats.forecast && trendInfo && !trendInfo.isStable && (
        <s.Group>
          <s.GroupTitle>Se continuar assim</s.GroupTitle>
          {[
            { key: 'in1h', label: 'em 1 hora' },
            { key: 'in4h', label: 'em 4 horas' },
            { key: 'in24h', label: 'em 1 dia' },
          ].map(({ key, label }) => {
            const f = stats.forecast[key];
            if (!f || !Number.isFinite(f.value)) return null;
            return (
              <s.Row
                key={key}
                title={`Projeção via tendência linear ± ${f.ci.toFixed(2)}. Guia, não garantia.`}
              >
                <s.Label>{label}</s.Label>
                <s.Value>
                  ~{formatNum(f.value)}
                  {unit && <small>{unit}</small>}
                  <small style={{ opacity: 0.6, marginLeft: 4 }}>
                    ±{formatNum(f.ci)}
                  </small>
                </s.Value>
              </s.Row>
            );
          })}
        </s.Group>
      )}

      {/* CORRELACOES — bar + badge de intensidade */}
      {correlations && correlations.length > 0 && (
        <s.Group>
          <s.GroupTitle>
            Movem juntas
            {primaryVarName && (
              <span style={{ fontWeight: 600, opacity: 0.6, marginLeft: 4 }}>
                com {primaryVarName}
              </span>
            )}
          </s.GroupTitle>
          {correlations.map((c) => {
            const info = classifyCorrelation(c.r);
            const SignIcon =
              c.r === null ? null : c.r >= 0 ? IconTrendUp : IconTrendDown;
            return (
              <s.CorrRow
                key={c.ihmname}
                title={
                  c.r === null
                    ? `Sem pontos com timestamps coincidentes`
                    : `n=${c.n} pares. ${info.strength} ${info.direction}.`
                }
              >
                <s.CorrHeader>
                  <s.ZoneDot color={c.color} />
                  <s.CorrLabel>{c.name}</s.CorrLabel>
                  <s.CorrValue
                    color={c.r === null ? undefined : c.color}
                  >
                    {SignIcon && <SignIcon />}
                    {c.r === null ? '—' : c.r.toFixed(2)}
                  </s.CorrValue>
                </s.CorrHeader>
                {c.r !== null && (
                  <>
                    <s.CorrBar>
                      <s.CorrBarFill color={c.color} $pct={info.abs * 100} />
                    </s.CorrBar>
                    <s.CorrBadge>
                      {info.strength} · {info.direction}
                    </s.CorrBadge>
                  </>
                )}
              </s.CorrRow>
            );
          })}
        </s.Group>
      )}

      {/* MUDANCAS BRUSCAS — top 5 com icone direcional */}
      {stats.changePoints && stats.changePoints.length > 0 && (
        <s.Group>
          <s.GroupTitle>Mudanças bruscas</s.GroupTitle>
          {stats.changePoints.slice(0, 5).map((cp) => {
            const isUp = cp.delta >= 0;
            const cpColor = isUp
              ? colors.orange || colors.red
              : colors.lightblue;
            return (
              <s.ChangeRow
                key={`cp-${cp.timestamp}`}
                title={`Significância ${cp.significance.toFixed(1)}σ`}
              >
                <s.ChangeIcon color={cpColor}>
                  {isUp ? <IconArrowUp /> : <IconArrowDown />}
                </s.ChangeIcon>
                <s.ChangeTime>{formatTs(cp.timestamp)}</s.ChangeTime>
                <s.ChangeDelta color={cpColor}>
                  {isUp ? '+' : ''}
                  {formatNum(cp.delta)}
                  {unit && <small>{unit}</small>}
                </s.ChangeDelta>
              </s.ChangeRow>
            );
          })}
          {stats.changePoints.length > 5 && (
            <s.Hint>+{stats.changePoints.length - 5} adicionais</s.Hint>
          )}
        </s.Group>
      )}

      {/* ZONAS — barra unica empilhada + legenda compacta */}
      {stats.zones && stats.zones.length > 0 && (
        <s.Group>
          <s.GroupTitle>Tempo em cada faixa</s.GroupTitle>
          <s.ZoneStackedBar>
            {stats.zones.map((zone) => (
              <s.ZoneStackedSegment
                key={zone.category}
                color={zone.color}
                $pct={zone.pct}
                title={`${zone.category}: ${zone.pct.toFixed(1)}% · ${formatDuration(
                  (stats.totalSpanMs * zone.pct) / 100
                )}`}
              />
            ))}
          </s.ZoneStackedBar>
          <s.ZoneLegend>
            {stats.zones.map((zone) => (
              <s.ZoneLegendRow key={zone.category}>
                <s.ZoneDot color={zone.color} />
                <s.ZoneLegendLabel>{zone.category}</s.ZoneLegendLabel>
                <s.ZoneLegendValue>{zone.pct.toFixed(1)}%</s.ZoneLegendValue>
                <s.ZoneLegendDuration>
                  {formatDuration((stats.totalSpanMs * zone.pct) / 100)}
                </s.ZoneLegendDuration>
              </s.ZoneLegendRow>
            ))}
          </s.ZoneLegend>
        </s.Group>
      )}

      {/* DISPONIBILIDADE — uptime como hero secundario */}
      {operationalAnalysis && (
        <s.Group>
          <s.GroupTitle>Disponibilidade</s.GroupTitle>
          <s.UptimeBig
            color={
              operationalAnalysis.uptimePct >= 90
                ? colors.green
                : operationalAnalysis.uptimePct >= 50
                ? colors.orange
                : colors.red
            }
          >
            {operationalAnalysis.uptimePct.toFixed(1)}
            <small>% rodando</small>
          </s.UptimeBig>
          <s.Row>
            <s.Label>Rodando</s.Label>
            <s.Value>
              {formatDuration(operationalAnalysis.totalRunningMs)}
            </s.Value>
          </s.Row>
          <s.Row>
            <s.Label>Parado</s.Label>
            <s.Value
              color={
                operationalAnalysis.totalStoppedMs > 0
                  ? colors.label
                  : undefined
              }
            >
              {formatDuration(operationalAnalysis.totalStoppedMs)}
            </s.Value>
          </s.Row>
          {operationalAnalysis.stoppedCount > 0 && (
            <>
              <s.Row title="Quantidade de períodos distintos em que a máquina ficou parada">
                <s.Label>Paradas</s.Label>
                <s.Value>{operationalAnalysis.stoppedCount}</s.Value>
              </s.Row>
              <s.Row title="Duração da maior parada contínua no período">
                <s.Label>Maior parada</s.Label>
                <s.Value>
                  {formatDuration(operationalAnalysis.biggestStoppedMs)}
                </s.Value>
              </s.Row>
            </>
          )}
        </s.Group>
      )}

      {/* ANOMALIAS — callout vermelho destacado */}
      {stats.outlierCount > 0 && (
        <s.Group>
          <s.GroupTitle>Anomalias</s.GroupTitle>
          <s.AnomalyCard
            title="Pontos a mais de 3 desvios-padrão da média — improváveis sob operação normal"
          >
            <s.AnomalyIcon>
              <IconAlert />
            </s.AnomalyIcon>
            <s.AnomalyText>
              <s.AnomalyCount>
                {stats.outlierCount.toLocaleString('pt-BR')} pontos fora do
                normal
              </s.AnomalyCount>
              <s.AnomalyBreakdown>
                {((stats.outlierCount / stats.count) * 100).toFixed(1)}% do
                período
                {stats.outlierHighCount > 0 &&
                  ` · ${stats.outlierHighCount} picos`}
                {stats.outlierLowCount > 0 &&
                  ` · ${stats.outlierLowCount} vales`}
              </s.AnomalyBreakdown>
            </s.AnomalyText>
          </s.AnomalyCard>
        </s.Group>
      )}

      {/* ANOTACOES — lista de pins do operador */}
      {annotations && annotations.length > 0 && (
        <s.Group>
          <s.GroupTitle>Anotações</s.GroupTitle>
          {annotations
            .slice()
            .sort((a, b) => b.timestamp - a.timestamp)
            .map((a) => (
              <s.NoteItem
                key={a.id}
                title={`Criado em ${new Date(a.createdAt).toLocaleString('pt-BR')}`}
              >
                <s.NoteIcon>
                  <IconNote />
                </s.NoteIcon>
                <s.NoteBody>
                  <s.NoteTime>{formatTs(a.timestamp)}</s.NoteTime>
                  <s.NoteText>{a.text}</s.NoteText>
                </s.NoteBody>
                {onRemoveAnnotation && (
                  <s.NoteDelete
                    type="button"
                    onClick={() => onRemoveAnnotation(a.id)}
                    aria-label="Remover anotação"
                    title="Remover"
                  >
                    ×
                  </s.NoteDelete>
                )}
              </s.NoteItem>
            ))}
        </s.Group>
      )}

      {/* JANELA — meta info compacta no rodape */}
      <s.FooterMeta>
        <span>{stats.count.toLocaleString('pt-BR')} amostras</span>
        <span>{formatDuration(stats.totalSpanMs)}</span>
      </s.FooterMeta>
    </s.Container>
  );
};

// React.memo: evita re-render quando parent re-renderiza por outros
// motivos. Props ja sao memoizadas — shallow compare suficiente.
export default React.memo(ChartStatsPanel);
